<template>
  <div class="mt-2">
    <div :style="[!this.noInnerWidth ? { width: width } : {}]">
      <div>
        <b-table
          :busy="isLoading"
          head-variant="light"
          :no-local-sorting="true"
          :items="items"
          :fields="getAllFields"
          :current-page="currentPage"
          per-page="0"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :sort-direction="sortDirection"
          :sticky-header="height"
          no-border-collapse
          responsive
          show-empty
          empty-text="Non ci sono informazioni da mostrare"
          small
          @sort-changed="sortingChanged"
        >
          <template #table-busy>
            <table-skeleton />
          </template>
          <template #empty>
            <template v-if="newCommunicationProcedureBtn">
              <div
                class="d-flex flex-column justify-content-center align-items-center my-3"
                style="max-width: max-content; margin-inline: auto"
              >
                <p class="mb-2">
                  Effettua una ricerca oppure, se non trovi risultati, crea una
                  nuova Procedura cliccando sul pulsante qui sotto
                </p>
                <b-button
                  class="btn-create px-3"
                  type="button"
                  variant="primary"
                  size="sm"
                  @click="onAddNewCommunicationProcedure"
                  title="Crea"
                  :disabled="!canVerb(resource, 'store')"
                  style="max-width: max-content"
                >
                  <b-icon icon="plus-circle" aria-hidden="true"></b-icon>
                  Crea
                </b-button
                >
              </div>
            </template>
            <template v-else>
              <empty-table
                :isTableInModal="isTableInModal"
                :performedSearch="performedSearch"
                :automaticPerformedSearch="automaticPerformedSearch"
                :filterCount="filterCount"
              />
            </template
            >
          </template>
          <template #cell(files)="{ item }" v-if="items.length">
            <div v-for="(document, index) in item.documents" :key="index">
              <span class="contact-icon" title="PDF"
                ><b-icon-file-earmark-pdf aria-hidden="true"
              /></span>
              <b-link href="download" @click.prevent="downloadDocument(document)">
                <span v-html="document.title"></span>
              </b-link>
            </div>
          </template>
          <template #cell(actions)="row">
            <div class="action-buttons">
              <b-button
                v-if="actions.includes('infomodal')"
                size="sm"
                variant="lisaweb"
                @click="openModal(row.item, row.index, $event.target)"
                class="mt-1 mr-1"
                title="Modale Dettagli"
              >
                <base-icon name="eye"></base-icon>
              </b-button>

              <b-button
                v-if="actions.includes('details')"
                size="sm"
                variant="lisaweb"
                @click="row.toggleDetails"
                class="mt-1 mr-1"
                :title="
                  row.detailsShowing ? 'Chiudi dettagli' : 'Apri dettagli'
                "
              >
                <b-icon v-if="row.detailsShowing" icon="chevron-up"></b-icon>
                <b-icon v-else icon="chevron-down"></b-icon>
              </b-button>

              <b-button
                v-if="actions.includes('edit')"
                size="sm"
                variant="lisaweb"
                @click="onEdit(row.item.id, row.item)"
                class="mt-1 mr-1"
                title="Riprendi"
                :disabled="isSent(row.item)"
              >
                <base-icon name="edit"></base-icon>
              </b-button>
              <b-button
                v-if="actions.includes('edit_schedule')"
                size="sm"
                variant="lisaweb"
                @click="onEditSchedule(row.item)"
                class="mt-1 mr-1"
                title="Modifica schedulazione"
                :disabled="!isSchedulable(row.item)"
              >
                <b-icon icon="clock"></b-icon>
              </b-button>
              <b-button
                v-if="actions.includes('retry_failed')"
                size="sm"
                variant="lisaweb"
                @click="onRetryFailed(row.item)"
                class="mt-1 mr-1"
                title="Ritrasmetti fallite"
                :disabled="!isRetryable(row.item)"
              >
                <b-icon icon="arrow-repeat"></b-icon>
              </b-button>
              <b-button
                v-if="actions.includes('view_logs')"
                size="sm"
                variant="lisaweb"
                @click="onViewLogs(row.item)"
                class="mt-1 mr-1"
                title="Vedi log"
                :disabled="!hasLogs(row.item)"
              >
                <b-icon icon="journal-text"></b-icon>
              </b-button>
              <b-button
                v-if="actions.includes('destroy')"
                size="sm"
                variant="lisaweb"
                @click="onDestroy(row.item.id)"
                class="mt-1 mr-1"
                title="Elimina"
                :disabled="isSent(row.item)"
              >
                <base-icon name="trash"></base-icon>
              </b-button>
            </div>
          </template>
          <template v-slot:[`cell(status_procedure.text)`]="{ item }">
            <status-badge kind="procedure" :status="item.status_procedure" />
          </template>
          <template #cell()="{ value }">
            <span>{{ value }}</span>
          </template>
        </b-table>
      </div>
      <pagination-skeleton v-if="isLoading && !noPagination" />
      <div
        class="pagination-wrapper"
        v-if="!isLoading && !noPagination && items.length"
      >
        <div class="info">
          <div class="show-text">
            <p>
              <label for="per-page-select-registry"
              >Numero di risultati per pagina:</label
              >
            </p>
          </div>
          <div class="group">
            <b-form-group label-for="per-page-select">
              <b-form-select
                id="per-page-select-registry"
                v-model="perPage"
                :options="pageOptions"
                style="width: 68px"
                size="sm"
                @change="onPageChange(1)"
                :disabled="!items.length"
              ></b-form-select>
            </b-form-group>
          </div>
          <div class="page">
            <p>
              Risultati da
              <strong>{{ formatItemsFrom }}</strong> a
              <strong>{{ formatItemsTo }}</strong> su un totale di
              <strong>{{ formatTotalItems }}</strong>
            </p>
          </div>
        </div>
        <div class="info-action">
          <div class="pagination" v-if="totalItems">
            <b-pagination
              v-model="currentPage"
              limit="3"
              :total-rows="totalItems"
              :per-page="perPage"
              size="sm"
              @change="onPageChange"
            >
              <template #page="{ page, active }">
                <span :data-active="active">{{
                    page.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")
                  }}</span>
              </template>
              <template v-slot:first-text>
                <span
                ><base-icon
                  scale="-1"
                  name="chevron_right_last_rounded"
                  width="16"
                  height="16"
                /></span>
              </template>
              <template v-slot:prev-text>
                <span
                ><base-icon
                  scale="-1"
                  name="chevron_right_rounded"
                  width="16"
                  height="16"
                /></span>
              </template>
              <template v-slot:next-text>
                <span
                ><base-icon
                  name="chevron_right_rounded"
                  width="16"
                  height="16"
                /></span>
              </template>
              <template v-slot:last-text>
                <span
                ><base-icon
                  name="chevron_right_last_rounded"
                  width="16"
                  height="16"
                /></span>
              </template>
            </b-pagination>
          </div>
          <div
            class="input_pagination"
            v-if="
              totalItems && !noInputPaginator && totalPages && totalPages > 3
            "
          >
            <base-input-pagination
              @pagination="onInputPagination"
            ></base-input-pagination>
          </div>
        </div>
      </div>
      <b-modal
        :id="infomodalName"
        @hidden="onModalHidden"
        :title="modalTitle"
        size="xl"
        scrollable
        ok-only
        ok-title="Chiudi"
        ok-variant="-transparent"
        header-bg-variant="lisaweb"
        no-close-on-backdrop
      >
        <div>
          <b-tabs content-class="pt-1" lazy v-model="tabIndex">
            <b-tab
              v-for="(tab, index) in tabs"
              :key="index"
              @click="setTab(tab, index)"
              :title="tab.text"
              :active="index === tabIndex"
              title-item-class="lisaweb"
            >
              <keep-alive>
                <component
                  v-if="selected === tab.name"
                  :is="selected"
                  :resourceId="modalResourceId"
                  @fetch="fetch"
                ></component>
              </keep-alive>
            </b-tab>
          </b-tabs>
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
import template from "./template";
import BaseIcon from "@/components/BaseIcon";
import StatusBadge from "@/components/StatusBadge";
import EmptyTable from "@/components/EmptyTable.vue";
import AutoRefreshMixin from "@/mixins/AutoRefreshMixin";
import moment from "moment";

/*
 * Ricarica automatica: si fa finché in pagina c'è una procedura che
 * LisaComunica cambierà, fino al suo stato finale:
 * - 6 Schedulato con l'ora di invio arrivata o vicina (scheduled_at vuoto,
 *   passato o imminente). LisaComunica la prende entro il minuto
 *   (procedures:dispatch-scheduled) e, finito di accodare i messaggi, scrive
 *   5 Invio o 8 Errore (StartProcedureJob::finish);
 * - 5 Invio: quando tutti i messaggi hanno l'esito (o il PDF è generato)
 *   LisaComunica scrive 9 Completato o 10 Completato con errori
 *   (ProcedureCompletion). Un invio grande dura ore (le email partono a
 *   distanza di qualche secondo l'una dall'altra): si segue finché l'ultimo
 *   aggiornamento della procedura ha meno di ACTIVE_SENDING_MAX_MS, oltre
 *   l'esito non arriverà più (es. procedure inviate prima degli stati 9 e 10).
 *
 * 0-4 sono stati del wizard; 7 Annullato, 8 Errore, 9 e 10 cambiano solo per
 * mano dell'operatore (Ritrasmetti fallite riporta a 5).
 * Si seguono anche gli Schedulati che partono entro ACTIVE_SCHEDULE_AHEAD_MS;
 * per quelli più lontani un timer risveglia il polling quando entrano nella
 * finestra (msUntilActiveRows), e uno scaduto da più di
 * ACTIVE_SCHEDULE_MAX_DELAY_MS vuol dire che l'esito non arriverà (l'avvio di
 * LisaComunica dura al massimo 40 minuti): lì si usa Ricarica.
 */
const SENDING = 5;
const SCHEDULED = 6;
const COMPLETED = 9;
const COMPLETED_WITH_ERRORS = 10;
const ACTIVE_SENDING_MAX_MS = 24 * 60 * 60 * 1000;
const ACTIVE_SCHEDULE_AHEAD_MS = 10 * 60 * 1000;
const ACTIVE_SCHEDULE_MAX_DELAY_MS = 60 * 60 * 1000;

export default {
  name: "CommunicationProceduresTable",
  extends: template,
  mixins: [AutoRefreshMixin],
  data() {
    return {};
  },
  computed: {
    formatItemsFrom() {
      if (!this.totalItems) return "";
      return this.items_from.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },
    formatItemsTo() {
      if (!this.totalItems) return "";
      return this.items_to.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },
    formatTotalItems() {
      if (!this.totalItems) return "";
      return this.totalItems.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },
  },
  props: {
    performedSearch: {
      type: Boolean,
      default: false,
    },
    filterCount: {
      type: Number,
      default() {
        return 0;
      },
    },
    isTableInModal: {
      type: Boolean,
      default: false,
    },
    saveRelationBtn: {
      type: Boolean,
      default: false,
    },
    hasChecksPaginated: {
      type: Boolean,
      default: false,
    },
    newCommunicationProcedureBtn: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    freeSearch() {
      this.$emit("manualFetch");
    },
    onAddNewCommunicationProcedure() {
      this.$router.push({
        name: `${this.resource}.create`,
        params: {
          /* remove the / character if it is the first character of a string */
          returnUrl: this.$route.path.replace(/^\/(.*)/, "$1"),
        },
      });
    },
    hasDocument(item) {
      return item?.documents?.length ? true : false;
    },
    /*
     * La schedulazione si tocca solo se c'è qualcosa da toccare: 6 Schedulato
     * (sposta o annulla) e 8 Errore (riprogramma). Su 5 Invio è troppo tardi,
     * negli stati del wizard non esiste ancora.
     *
     * 7 Annullato è escluso di proposito, non per dimenticanza: l'invio è stato
     * revocato, riprenderlo vuol dire rifare le scelte della procedura, quindi
     * si passa da "Riprendi" e dal wizard. Qui si modifica una schedulazione che
     * esiste ancora, non se ne crea una nuova.
     */
    isSchedulable(item) {
      return [6, 8].includes(item?.status_procedure?.value);
    },
    onEditSchedule(item) {
      this.$emit("editSchedule", item);
    },
    /*
     * Si ritrasmette solo ciò che è stato davvero inviato: 5 Invio, 8 Errore,
     * 9 Completato e 10 Completato con errori, e solo per Email (0) e SMS (2):
     * la Stampa (3) non ha messaggi da ritrasmettere.
     */
    isRetryable(item) {
      return this.hasLogs(item);
    },
    // Messaggi su LisaComunica: solo procedure email e SMS inviate (non stampa).
    hasLogs(item) {
      return (
        [SENDING, 8, COMPLETED, COMPLETED_WITH_ERRORS].includes(
          item?.status_procedure?.value,
        ) && [0, 2].includes(item?.communication_type?.value)
      );
    },
    // Inviata (anche se conclusa): non si riprende né si elimina.
    isSent(item) {
      return [SENDING, SCHEDULED, COMPLETED, COMPLETED_WITH_ERRORS].includes(
        item?.status_procedure?.value,
      );
    },
    onRetryFailed(item) {
      this.$emit("retryFailed", item);
    },
    onViewLogs(item) {
      this.$emit("viewLogs", item);
    },
    downloadDocument(item) {
      this.$emit("download", item);
    },
    /* Aggiornamento dinamico (AutoRefreshMixin): vedi il commento in testa */
    hasActiveRows() {
      const now = moment();
      return this.items.some((item) => {
        if (item?.status_procedure?.value === SENDING) {
          // last_update è l'ora di Roma senza fuso, come scheduled_at
          return (
            !item.last_update ||
            now.diff(moment(item.last_update)) <= ACTIVE_SENDING_MAX_MS
          );
        }
        if (item?.status_procedure?.value !== SCHEDULED) return false;
        if (!item.scheduled_at) return true;
        // scheduled_at è l'ora di Roma senza fuso, come la mostra la tabella
        const delay = now.diff(moment(item.scheduled_at));
        return (
          delay >= -ACTIVE_SCHEDULE_AHEAD_MS &&
          delay <= ACTIVE_SCHEDULE_MAX_DELAY_MS
        );
      });
    },
    // uno Schedulato oltre la finestra: quando ci entra, il polling riparte
    msUntilActiveRows() {
      const now = moment();
      const waits = this.items
        .filter(
          (item) =>
            item?.status_procedure?.value === SCHEDULED && item.scheduled_at,
        )
        .map(
          (item) =>
            moment(item.scheduled_at).diff(now) - ACTIVE_SCHEDULE_AHEAD_MS,
        )
        .filter((ms) => ms > 0);
      return waits.length ? Math.min(...waits) : null;
    },
    silentFetch() {
      return this.fetch(undefined, true);
    },
    isFirstPage() {
      // la pagina può arrivare dalla paginazione salvata, non da currentPage
      return Number(this.items_from) === 1;
    },
    rowSignature(item) {
      return [item.id, item?.status_procedure?.value];
    },
  },
  components: {
    BaseIcon,
    StatusBadge,
    EmptyTable,
  },
};
</script>

<style lang="css" scoped></style>
